<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <page-header>
      {{ $t('AdvancedAnalysis') }}
    </page-header>
    <div class="mt-4">
      <loading-provider :loading="loading">
        <v-card
          v-for="(t,i) in tableData"
          :key="i"
          class="preventTableFont pa-4 mb-2 d-flex align-center text-body-1"
          color="grey lighten-3"
          elevation="0"
          @click="selectCard(t)"
        >
          <div class="flex-grow-1">
            <div>
              <span class="indigo--text font-weight-black">{{ t.totalCount }} × </span>{{ t.name }}
            </div>
            <div class="text-body-2">
              {{ t.code }} / Variants: {{ t.variants.length }}
            </div>
          </div>

          <div class="text-h4">
            {{ t.price }}
          </div>
        </v-card>
      </loading-provider>
    </div>
    <v-dialog
      v-model="showDetail"
      max-width="600"
    >
      <v-card
        v-if="activeCard"
        class="pa-6"
      >
        <div class="text-h4">
          {{ $t('Details') }}
        </div>
        <v-card
          class="pa-4 mb-2 mt-8 d-flex align-center text-body-1"
          color="grey lighten-3"
          elevation="0"
        >
          <div class="flex-grow-1">
            <div>
              <span class="indigo--text font-weight-black">{{ activeCard.totalCount }} × </span>{{ activeCard.name }}
            </div>
          </div>

          <div class="text-h4">
            {{ activeCard.price }}
          </div>
        </v-card>
        <v-card
          v-for="v in activeCard.variants"
          :key="v.name"
          class="pa-4 mb-2 d-flex align-center text-body-1"
          color="grey lighten-4"
          elevation="0"
        >
          <div class="flex-grow-1">
            <div><span class="indigo--text font-weight-black">{{ v.count }} × </span> {{ v.name }}</div>
            <div class="text-body-2 mt-2">
              {{ v.mod }}
            </div>
          </div>
          <div class="text-no-wrap font-weight-black ml-4">
            {{ v.price |priceDisplay }}
          </div>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>

import { IKUtils } from 'innerken-js-utils'
import Attribute from '@/model/dataLayer/api/dish/Attribute'
import AttributeGroup from '@/model/dataLayer/api/dish/AttributeGroup'
import { salesInsight } from '@/model/dataLayer/api/analysis'
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import { orderBy } from 'lodash-es'
import { DishDocker } from 'aaden-base-model'
import { formatPrice } from '@/model/utils/formatPrice'

export default {
  name: 'SalesInsight',
  components: {
    LoadingProvider,
    PageHeader
  },
  data: function () {
    return {
      displayData: [],
      attributeData: [],
      activeCard: null,
      showDetail: false,
      loading: false
    }
  },
  computed: {
    headers () {
      return [
        {
          text: 'name',
          value: 'name'
        },
        {
          text: 'price',
          value: 'price'
        },
        {
          text: 'amount',
          value: 'amount'
        }
      ].concat(this.attributeHeader)
    },
    realAttributes () {
      return this.attributeData.reduce((arr, a) => {
        if (a.childAttributes) {
          a.childAttributes.forEach(c => arr.push(c))
        }

        return arr
      }, [])
    },
    attributeHeader () {
      return this.realAttributes
        .map(a => {
          a.text = a.name
          a.value = 'attr' + a.id
          return a
        })
    },
    tableData () {
      const formatDish = (d) => {
        d.isDish = true
        d.price = `${formatPrice(d.originPrice)} (avg:${formatPrice(d.totalPrice / d.totalCount)})`
        d.amount = d.totalCount
        d.groupData.forEach(g => {
          d['attr' + g.id] = g.count
        })
        d.variants = d.variants.map(formatVariant)
        return d
      }
      const formatVariant = (v, i) => {
        v.name = 'v:' + i
        v.amount = v.count
        v.mod = v.groupData.filter(it => it.count > 0).map(it => it.count + '× ' + it.name).join(',')
        v.groupData.forEach(g => {
          v['attr' + g.id] = g.count
        })
        return v
      }

      return orderBy(this.displayData.map(formatDish), ['amount'], ['desc'])
    }
  },
  watch: {
    '$parent.dateRange': {
      handler: function (val) {
        this.loadData()
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    await this.loadData()
  },
  methods: {
    groupElement (childList, childMatchKey) {
      const on = function (parentList, matchKey) {
        const _parentList = IKUtils.deepCopy(parentList)
        const saveIn = function (resultKey) {
          childList.forEach(c => {
            const target = _parentList.find(p => c[childMatchKey] === p[matchKey])
            if (target) {
              if (!target[resultKey]) {
                target[resultKey] = []
              }
              target[resultKey].push(c)
            }
          })
          return _parentList
        }
        return { saveIn }
      }
      return { on }
    },
    async getAllAttribute () {
      return (await Attribute.getList())
        .filter(a => a.optattributeGroupId.length > 0)
    },
    async getAttributeGrouped () {
      const attributeGroup = await AttributeGroup.getList()
      const attribute = await this.getAllAttribute()
      return this.groupElement(attribute, 'attributeGroupId')
        .on(attributeGroup, 'id').saveIn('childAttributes')
    },
    async getSalesInfo (timeSpan) {
      this.loading = true
      const data = await salesInsight(timeSpan)
      if (data.length === 0) {
        this.loading = false
        return []
      }
      data.forEach(it => {
        if (!it.currentName) {
          it.currentName = it.currentNameDE
        }
      })
      const dishList = DishDocker.StandardDishesListFactory()
        .formatList((data))
      const displayList = dishList.reduce((obj, i) => {
        let dict = obj[i.code]
        if (!dict) {
          obj[i.code] = {
            categoryId: parseInt(i.categoryId),
            categoryTypeId: parseInt(i.categoryTypeId),
            code: i.code,
            // name: i.code + '/' + i.name,
            name: i.name,
            totalCount: i.count,
            originPrice: i.originPrice,
            totalPrice: i.realPrice * i.count,
            variants: []
          }
          dict = obj[i.code]
        } else {
          dict.totalCount += i.count
          dict.totalPrice += i.realPrice * i.count
        }

        const variant = dict.variants.find(v => v.identity === i.identity)
        if (variant) {
          variant.count += i.count
        } else {
          dict.variants.push({
            tempDiscountMod: i.tempDiscountMod,
            orderPriceMod: i.orderPriceMod,
            identity: i.identity,
            count: parseInt(i.count),
            displayApply: i.displayApply,
            price: i.realPrice
          })
        }
        return obj
      }, {})
      this.loading = false
      return Object.values(displayList).map(u => {
        u.group = this.groupAttribute(u)
        return u
      }).filter(it => it.totalCount !== 0)
    },
    groupAttribute (dish) {
      // group key
      dish.groupData = IKUtils.deepCopy(this.realAttributes)
      dish.variants.forEach(v => {
        v.groupData = IKUtils.deepCopy(this.realAttributes)
        v.displayApply.forEach(displayApply => {
          const child = dish.groupData.find(x => x.id === parseInt(displayApply.aId))
          const selfChild = v.groupData.find(x => x.id === parseInt(displayApply.aId))
          if (!child || !selfChild) {
            return
          }
          if (!selfChild.count) {
            selfChild.count = 0
          }
          selfChild.count++
          if (!child.count) {
            child.count = 0
          }
          child.count++
        })
      })
      return dish.variants.reduce((arr, v) => {
        v.displayApply.forEach(displayApply => {
          let group = arr.find(a => a.agId === displayApply.agId)
          if (!group) {
            group = {
              ...displayApply,
              values: []
            }
            arr.push(group)
          }
          const child = group.values.find(x => x.aId === displayApply.aId)
          if (child) {
            child.count++
          } else {
            group.values.push({
              ...displayApply,
              count: 1
            })
          }
        })
        return arr
      }, [])
    },
    async loadSalesData () {
      const salesData = await this.getSalesInfo(this.$parent.dateRange)
      const totalData = salesData.reduce((obj, i) => {
        obj.totalPrice += i.totalPrice
        obj.groupData.forEach((g, t) => {
          if (!g.count) {
            g.count = 0
          }
          g.count += (i.groupData[t].count ?? 0)
        })
        obj.totalCount += i.totalCount
        return obj
      }, {
        code: '-',
        name: 'Alle',
        totalPrice: 0,
        originPrice: 0,
        totalCount: 0,
        groupData: IKUtils.deepCopy(this.realAttributes),
        variants: []
      })
      this.displayData = [totalData].concat(salesData)
    },
    selectCard (card) {
      this.activeCard = card
      this.showDetail = true
    },
    async loadData () {
      this.attributeData = await this.getAttributeGrouped()
      await this.loadSalesData()
    }
  }
}
</script>

<style>
.bigHeadHorizontal {
  background: #367aeb;
  font-size: 24px;
  padding: 8px 2px;
  color: white;
  grid-row: 1 / span 2;
}

.agHead {
  background: #4caf50;
  color: white;
  padding: 8px 2px;
}

.dishRow > td {
  font-weight: bold !important;
  font-size: 18px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preventTableFont th {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px !important;
}

.dishRow {
  background: #f6f6f6;
  color: black;
}

.variantRow {
  padding: 4px;
}

.attributeGrid {
  border-right: 1px dotted #333333;
}

.aHead {
  padding: 2px;

}
</style>
